/**
 * nexusFetch — the one fetch wrapper every admin-ui page uses to talk to
 * the Nexus gateway.
 *
 * Concerns it owns:
 *   - Prepends the configured `apiBase` (so pages call '/admin/broker-creds'
 *     and the host SPA decides whether that resolves to /api/admin/...
 *     via Caddy reverse-proxy or a fully-qualified gateway URL).
 *   - Adds `Authorization: Bearer <token>` when the host's getAuthToken()
 *     returns a non-empty string. Read fresh on every call so token
 *     rotation Just Works.
 *   - Defaults Content-Type: application/json on POST/PATCH/PUT/DELETE
 *     when the caller didn't set one and the body is present.
 *   - Parses the JSON response and returns it typed as T.
 *   - Throws NexusAdminApiError on non-2xx with the parsed body when it's
 *     shaped like the standard Nexus error envelope { code, message, errors? }.
 *
 * Two ways to call it from a page:
 *
 *   // Option A — explicit context (works inside non-hook helpers):
 *   import { useNexusAdmin, nexusFetch } from '@nexus/admin-ui';
 *   const ctx = useNexusAdmin();
 *   const data = await nexusFetch<{ ok: true }>('/admin/health', { ctx });
 *
 *   // Option B — bound helper (cleanest inside components):
 *   const fetch = useNexusFetch();
 *   const data = await fetch<{ ok: true }>('/admin/health');
 */
import { useCallback } from 'react';
import { useNexusAdmin } from './provider.js';
/**
 * Thrown by nexusFetch for any non-2xx response. The body is parsed when
 * it's JSON; otherwise `code` falls back to 'HTTP_<status>' and `message`
 * to the response statusText.
 */
export class NexusAdminApiError extends Error {
    status;
    code;
    errors;
    body;
    constructor(payload, body) {
        super(payload.message);
        this.name = 'NexusAdminApiError';
        this.status = payload.status;
        this.code = payload.code;
        this.errors = payload.errors;
        this.body = body;
    }
}
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
async function readJson(res) {
    const text = await res.text();
    if (!text)
        return undefined;
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
/**
 * Low-level fetch — usable outside React (tests, scripts) by passing
 * `ctx` explicitly. Inside components prefer `useNexusFetch()`.
 */
/** Window event fired on 403 ADMIN_MFA_REQUIRED when the host passed no onAdminMfaRequired. */
export const ADMIN_MFA_EVENT = 'nexus:admin-mfa-required';
/**
 * Tell the host that platform-admin power needs a passkey step-up: its
 * onAdminMfaRequired handler, else a window event (ADMIN_MFA_EVENT) it can listen to.
 * Used by nexusFetch and by non-fetch callers (the remote-access terminal WebSocket).
 */
export function notifyAdminMfaRequired(ctx) {
    try {
        if (ctx.onAdminMfaRequired)
            ctx.onAdminMfaRequired();
        else if (typeof window !== 'undefined')
            window.dispatchEvent(new window.CustomEvent(ADMIN_MFA_EVENT));
    }
    catch { /* a host handler must never mask the request's own error */ }
}
export async function nexusFetch(path, init = {}) {
    return nexusFetchOnce(path, init, false, false);
}
async function nexusFetchOnce(path, init, retried, steppedUp) {
    const { ctx, json, body, headers, method, ...rest } = init;
    if (!ctx) {
        throw new Error('nexusFetch: missing ctx. Either pass { ctx } explicitly or use useNexusFetch() inside a component.');
    }
    const url = `${ctx.apiBase}${path.startsWith('/') ? path : `/${path}`}`;
    const resolvedMethod = method ?? (json !== undefined || body !== undefined ? 'POST' : 'GET');
    const finalHeaders = {};
    if (headers) {
        // Normalize HeadersInit shapes into a plain record.
        if (headers instanceof Headers) {
            headers.forEach((v, k) => {
                finalHeaders[k] = v;
            });
        }
        else if (Array.isArray(headers)) {
            for (const [k, v] of headers)
                finalHeaders[k] = v;
        }
        else {
            Object.assign(finalHeaders, headers);
        }
    }
    const token = ctx.getAuthToken();
    if (token)
        finalHeaders['Authorization'] = `Bearer ${token}`;
    let finalBody = body;
    if (json !== undefined) {
        finalBody = JSON.stringify(json);
        if (!finalHeaders['Content-Type'] && !finalHeaders['content-type']) {
            finalHeaders['Content-Type'] = 'application/json';
        }
    }
    else if (MUTATING_METHODS.has(resolvedMethod) &&
        finalBody &&
        !finalHeaders['Content-Type'] &&
        !finalHeaders['content-type'] &&
        typeof finalBody === 'string') {
        finalHeaders['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, {
        ...rest,
        method: resolvedMethod,
        headers: finalHeaders,
        body: finalBody,
    });
    if (!res.ok) {
        const body = await readJson(res);
        const payload = {
            status: res.status,
            code: body?.code ?? `HTTP_${res.status}`,
            message: body?.message ?? `${res.status} ${res.statusText}`,
            errors: body?.errors,
        };
        // An expired access token: let the host refresh it once (keeping the session's
        // passkey step-up), then retry the same request.
        if (res.status === 401 && !retried && ctx.refreshAccessToken
            && (payload.code === 'TOKEN_EXPIRED' || payload.code === 'INVALID_TOKEN')) {
            let fresh = null;
            try {
                fresh = await ctx.refreshAccessToken();
            }
            catch {
                fresh = null;
            }
            if (fresh)
                return nexusFetchOnce(path, init, true, steppedUp);
        }
        // NEXUS-586: admin power needs a passkey step-up for this session — let the host route
        // to its step-up screen (the error still reaches the caller).
        if (res.status === 403 && payload.code === 'ADMIN_MFA_REQUIRED') {
            // The host's "Confirm it's you" dialog: one passkey prompt, then this request again.
            if (ctx.requestStepUp && !steppedUp) {
                let ok = false;
                try {
                    ok = await ctx.requestStepUp();
                }
                catch {
                    ok = false;
                }
                if (ok)
                    return nexusFetchOnce(path, init, retried, true);
            }
            else if (!ctx.requestStepUp) {
                notifyAdminMfaRequired(ctx);
            }
        }
        throw new NexusAdminApiError(payload, body);
    }
    // 204 / empty body → return undefined as T.
    if (res.status === 204)
        return undefined;
    const parsed = await readJson(res);
    return (parsed ?? undefined);
}
/**
 * Returns a bound nexusFetch that auto-supplies the current context.
 * Use this inside components — it's the most ergonomic call site.
 */
export function useNexusFetch() {
    const ctx = useNexusAdmin();
    return useCallback((path, init = {}) => nexusFetch(path, { ...init, ctx }), [ctx]);
}
//# sourceMappingURL=api.js.map